import { makeAutoObservable, runInAction, computed } from 'mobx';
import { Review, ReviewStats } from '../model/professional/reviews-model';
import userStore from './session-user-store';

class ReviewsStore {
  // État des avis
  reviews: Review[] = [];
  stats: ReviewStats | null = null;
  count: number = 0;
  isLoading = false;
  error: string | null = null;
  professionalId: string | null = null;

  constructor() {
    makeAutoObservable(this);
    
    // Récupérer l'ID du professionnel depuis le store utilisateur
    this.professionalId = userStore.authUser?.user?.id || null;
  }

  /**
   * Méthode pour définir les avis
   */
  setReviews(reviews: Review[]) {
    runInAction(() => {
      this.reviews = reviews;
    });
  }

  /**
   * Méthode pour définir le nombre total d'avis
   */
  setCount(count: number) {
    runInAction(() => {
      this.count = count;
    });
  }

  /**
   * Méthode pour définir les statistiques d'avis
   */
  setStats(stats: ReviewStats) {
    runInAction(() => {
      this.stats = stats;
    });
  }

  /**
   * Méthode pour obtenir un avis par son ID
   */
  getReviewById(reviewId: string): Review | undefined {
    return this.reviews.find(review => review.id === reviewId);
  }
  
  /**
   * Méthode pour mettre à jour un avis
   */
  updateReview(updatedReview: Review) {
    runInAction(() => {
      const index = this.reviews.findIndex(review => review.id === updatedReview.id);
      if (index !== -1) {
        this.reviews = [
          ...this.reviews.slice(0, index),
          updatedReview,
          ...this.reviews.slice(index + 1)
        ];
      }
    });
  }
  
  /**
   * Méthode pour ajouter un avis
   */
  addReview(review: Review) {
    runInAction(() => {
      this.reviews = [review, ...this.reviews];
      this.count += 1;
      
      // Mettre à jour les statistiques si elles existent
      if (this.stats) {
        const newTotalRating = (this.stats.averageRating * this.stats.count) + review.rating;
        const newCount = this.stats.count + 1;
        const newAverage = newTotalRating / newCount;
        
        // Mettre à jour la distribution des notes
        const newDistribution = { ...this.stats.ratingDistribution };
        if (newDistribution) {
          newDistribution[review.rating] = (newDistribution[review.rating] || 0) + 1;
        }
        
        this.stats = {
          ...this.stats,
          averageRating: newAverage,
          count: newCount,
          ratingDistribution: newDistribution
        };
      }
    });
  }

  /**
   * Getter pour tous les avis
   */
  @computed
  get allReviews() {
    return this.reviews;
  }
  
  /**
   * Getter pour les avis avec réponse
   */
  @computed
  get reviewsWithResponse() {
    return this.reviews.filter(review => review.response && review.response.trim() !== '');
  }
  
  /**
   * Getter pour les avis sans réponse
   */
  @computed
  get reviewsWithoutResponse() {
    return this.reviews.filter(review => !review.response || review.response.trim() === '');
  }
  
  /**
   * Getter pour la note moyenne
   */
  @computed
  get averageRating() {
    return this.stats?.averageRating || 0;
  }
}

const reviewsStore = new ReviewsStore();
export default reviewsStore;
